/**
 * The shell: route table, permission gate, and the app chrome.
 *
 * Routes are DATA, not JSX, so the nav and the router read the same table —
 * the shipped app had them drift, which is how a menu ends up advertising a
 * page the backend then refuses.
 */
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthProvider";
import { LoginPage } from "@/pages/login/LoginPage";
import { AppShell } from "./AppShell";
import { ROUTES } from "./routes";

const NotFound = lazy(() => import("@/pages/NotFound"));
const ActivatePage = lazy(() => import("@/pages/activate/ActivatePage"));

function FullPageSpinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <p className="text-sm text-text-muted">{label}</p>
    </div>
  );
}

export function App() {
  const { status, canAny, user } = useAuth();

  // PUBLIC, and checked before the session gate. /activate?token=… is handed
  // out to people who do not have an account yet; falling through to the
  // anonymous branch would drop the token on the login screen and leave them
  // with nowhere to go.
  if (window.location.pathname === "/activate") {
    return (
      <Suspense fallback={<FullPageSpinner label="Chargement…" />}>
        <Routes>
          <Route path="/activate" element={<ActivatePage />} />
        </Routes>
      </Suspense>
    );
  }

  if (status === "loading") return <FullPageSpinner label="Chargement…" />;
  if (status === "anonymous") {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <AppShell>
      <Suspense fallback={<FullPageSpinner label="Chargement de la page…" />}>
        <Routes>
          {ROUTES.map((route) => {
            // Deny by default: a route with permission codes is only mounted
            // when the manifest grants at least one of them. Anything else
            // falls through to the 404 below, so an un-granted page is
            // indistinguishable from a page that does not exist.
            const allowed = (!route.perms?.length || canAny(route.perms))
              && (!route.roles?.length || route.roles.includes(user?.role ?? ""));
            if (!allowed) return null;
            const Page = route.component;
            return <Route key={route.path} path={route.path} element={<Page />} />;
          })}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
